import { Box, Button, FormLabel, HStack, Text, VStack } from '@chakra-ui/react';
import { RepeatIcon, CopyIcon } from '@chakra-ui/icons';
import { type FunctionsError, httpsCallable } from 'firebase/functions';
import { useState, useContext } from 'react';
import { useToast } from '@chakra-ui/react';
import type League from '@/models/league';
import { firebaseErrorToastOptions } from '@/helpers';
import { FirebaseContext } from '@/session/FirebaseContext';
import { pageBodyTextProps, pageButtonProps, pageMetaTextProps, pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from './Card';

interface InviteCardProps {
  id: string;
  isAdmin: boolean;
  copyString: (s: string) => Promise<void>;
  league: League;
  /** When true, omit outer card chrome (content sits inside a profile-style card). */
  embedded?: boolean;
}

export function InviteCard(props: InviteCardProps) {
  const ctx = useContext(FirebaseContext);
  const { id, isAdmin, copyString, league, embedded = false } = props;
  const toast = useToast();
  const [isRefreshingPin, setIsRefreshingPin] = useState(false);
  const joinLink = `${window.location.host}/join/${league.authCode}`;

  async function RefreshPin() {
    setIsRefreshingPin(true);
    try {
      await httpsCallable(ctx.funcs, 'resetLeaguePin')({ leagueUID: id });
    } catch (error) {
      toast(firebaseErrorToastOptions('Failed To Refresh Pin', error as FunctionsError));
    } finally {
      setIsRefreshingPin(false);
    }
  }

  const body = (
    <VStack align='stretch' spacing={3} w='full'>
      <Text {...pageSectionTitleProps}>Invite</Text>
      <Text {...pageBodyTextProps} color='gray.500'>
        Share this league with friends.
      </Text>

      <Box>
        <FormLabel {...pageMetaTextProps} mb={1}>
          Pin
        </FormLabel>
        <HStack spacing={2} flexDirection={{ base: 'column', sm: 'row' }} align='stretch'>
          <Button
            {...pageButtonProps}
            flex={1}
            w={{ base: 'full', sm: undefined }}
            variant='outline'
            rightIcon={<CopyIcon boxSize={3} />}
            onClick={() => void copyString(league.authCode)}
          >
            {league.authCode}
          </Button>
          {isAdmin ? (
            <Button
              {...pageButtonProps}
              w={{ base: 'full', sm: 'auto' }}
              variant='outline'
              isLoading={isRefreshingPin}
              onClick={() => void RefreshPin()}
              rightIcon={<RepeatIcon boxSize={3} />}
            >
              Refresh
            </Button>
          ) : null}
        </HStack>
      </Box>

      <Box>
        <FormLabel {...pageMetaTextProps} mb={1}>
          Link
        </FormLabel>
        <Button
          {...pageButtonProps}
          w='full'
          variant='outline'
          rightIcon={<CopyIcon boxSize={3} />}
          onClick={() => void copyString(joinLink)}
          justifyContent='space-between'
        >
          <Text as='span' fontSize='xs' noOfLines={1} textAlign='left' flex={1} mr={2}>
            {joinLink}
          </Text>
        </Button>
      </Box>
    </VStack>
  );

  if (embedded) {
    return body;
  }

  return <Card>{body}</Card>;
}
